/**
 * 요금제 가격 버전
 * ---------------------------------------------------------------------------
 * 구독 중인 기업이 있으면 가격을 그 자리에서 못 고친다 — 이미 발행한 청구서의
 * 근거가 달라진다. 대신 **같은 코드로 시행일만 다른 버전**을 만들고 기업을
 * 하나씩 옮긴다.
 *
 * 이 창이 답해야 하는 것
 *   1. 지금까지 가격이 어떻게 바뀌었나 (버전 목록)
 *   2. 어느 버전에 몇 곳이 남아 있나 (옮기지 않은 기업이 있으면 못 닫는다)
 *   3. 새 가격은 언제부터 얼마인가
 *
 * 옛 버전을 자동으로 닫지 않는다
 *   닫으면 아직 안 옮긴 구독이 "만료된 요금제" 를 가리킨다. 다 옮긴 뒤
 *   운영자가 판매중지한다.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface PlanVersion {
  planId: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  basePrice: number;
  unitPrice: number;
  isActive: boolean;
  activeSubscriptions: number;
  isCurrent: boolean;
}

const won = (n: number) => `${Math.round(n).toLocaleString('ko-KR')}원`;

export function PlanVersionModal({
  plan,
  onClose,
}: {
  plan: { plan_id: string; plan_code: string; plan_name: string; base_price: number; unit_price: number } | null;
  onClose: () => void;
}) {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const versions = useQuery<PlanVersion[], NormalizedError>({
    queryKey: ['admin', 'plans', plan?.plan_id, 'versions'],
    enabled: Boolean(plan),
    queryFn: async () =>
      (await apiClient.get<PlanVersion[]>(`/admin/plans/${plan!.plan_id}/versions`)).data,
  });

  useEffect(() => {
    if (!plan) return;
    setError(null);
    form.setFieldsValue({
      // 다음 달 1일이 기본이다. 청구 주기의 경계라 계산이 깔끔하다.
      effectiveFrom: dayjs().add(1, 'month').startOf('month'),
      basePrice: plan.base_price,
      unitPrice: plan.unit_price,
    });
  }, [plan, form]);

  const save = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values || !plan) return;

    setSaving(true);
    setError(null);
    try {
      await apiClient.post(`/admin/plans/${plan.plan_id}/versions`, {
        effectiveFrom: values.effectiveFrom.format('YYYY-MM-DD'),
        basePrice: Number(values.basePrice),
        unitPrice: Number(values.unitPrice),
      });
      message.success('새 가격 버전을 만들었습니다. 기업을 옮긴 뒤 옛 버전을 판매중지하세요.');
      await client.invalidateQueries({ queryKey: ['admin', 'plans'] });
      onClose();
    } catch (err) {
      setError(errorText(err as NormalizedError));
    } finally {
      setSaving(false);
    }
  };

  const stillOnOld = (versions.data ?? []).filter(
    (v) => !v.isCurrent && v.activeSubscriptions > 0,
  );

  return (
    <Modal
      open={Boolean(plan)}
      title={plan ? `${plan.plan_name} — 가격 버전` : '가격 버전'}
      okText="새 버전 만들기"
      cancelText="닫기"
      confirmLoading={saving}
      width={720}
      onCancel={onClose}
      onOk={() => void save()}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {error && <Alert type="error" showIcon message={error} />}

        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          같은 코드(<Text code>{plan?.plan_code}</Text>)로 시행일만 다른 버전을 만듭니다. 모듈과
          한도는 그대로 복사되고 <strong>가격만</strong> 달라집니다. 기존 구독의 가격은 바뀌지
          않습니다 — 「과금 관리」에서 기업을 새 버전으로 옮겨야 적용됩니다.
        </Paragraph>

        <Table<PlanVersion>
          rowKey="planId"
          size="small"
          pagination={false}
          loading={versions.isLoading}
          dataSource={versions.data ?? []}
          locale={{ emptyText: '버전이 없습니다.' }}
          columns={[
            {
              title: '시행일',
              dataIndex: 'effectiveFrom',
              width: 130,
              render: (v: string, row) => (
                <Space size={4}>
                  <Text>{v}</Text>
                  {row.isCurrent && <Tag color="blue">현재</Tag>}
                </Space>
              ),
            },
            {
              title: '종료일',
              dataIndex: 'effectiveTo',
              width: 110,
              render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
            },
            { title: '기본료', dataIndex: 'basePrice', width: 120, render: won },
            { title: '단위당', dataIndex: 'unitPrice', width: 110, render: won },
            {
              title: '구독',
              dataIndex: 'activeSubscriptions',
              width: 90,
              render: (v: number) =>
                v > 0 ? <Tag color="green">{v}곳</Tag> : <Text type="secondary">—</Text>,
            },
            {
              title: '판매',
              dataIndex: 'isActive',
              width: 80,
              render: (v: boolean) => (v ? <Tag color="green">중</Tag> : <Tag>중지</Tag>),
            },
          ]}
        />

        {stillOnOld.length > 0 && (
          <Alert
            type="info"
            showIcon
            message="옛 버전에 남아 있는 기업이 있습니다"
            description={
              <>
                {stillOnOld
                  .map((v) => `${v.effectiveFrom} 시행 ${v.activeSubscriptions}곳`)
                  .join(', ')}
                . 다 옮기기 전에는 그 버전을 판매중지하지 마세요 — 구독이 만료된 요금제를
                가리키게 됩니다.
              </>
            }
          />
        )}

        <Form form={form} layout="vertical" requiredMark={false}>
          <Space size="middle" wrap style={{ display: 'flex' }} align="start">
            <Form.Item
              name="effectiveFrom"
              label="시행일"
              rules={[{ required: true, message: '시행일을 고르세요' }]}
              extra="오늘 이후만 고를 수 있습니다"
            >
              <DatePicker
                style={{ width: 170 }}
                // 지난 날짜로 가격을 만들면 이미 발행한 청구서의 근거와 어긋난다.
                disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
              />
            </Form.Item>
            <Form.Item
              name="basePrice"
              label="기본료(원)"
              rules={[{ required: true, message: '기본료를 입력하세요' }]}
            >
              <InputNumber min={0} step={1000} style={{ width: 160 }} />
            </Form.Item>
            <Form.Item name="unitPrice" label="단위당(원)">
              <InputNumber min={0} step={100} style={{ width: 160 }} />
            </Form.Item>
          </Space>
        </Form>
      </Space>
    </Modal>
  );
}
